<template>
  <div>
    <v-row>
      <v-col>
        <div class="text-h6 mt-3 mb-4">Réassigner cette inscription à un compte utilisateur</div>
        <p>
          Associez l'inscription de <b>{{ inscription.nom }} {{ inscription.prenom }}</b> et la commande associée à un
          compte utilisateur existant. Cette action permettra au titulaire du compte d'accéder à cette inscription
          depuis
          son espace personnel.
        </p>

        <v-row class="mt-4">
          <v-col md="8">
            <label class="field_label mb-2 d-block">Rechercher un compte par adresse email</label>
            <div class="d-flex ga-2 align-center">
              <v-text-field v-model="searchEmail" label="Adresse email" density="compact" hide-details
                @keyup.enter="searchAccounts" @update:model-value="onSearchInput"></v-text-field>
              <SportipsButton icon="mdi-magnify" @click="searchAccounts" :disabled="searchEmail.length < 2 || loading">
                Rechercher
              </SportipsButton>
            </div>

            <v-progress-linear v-if="loading" indeterminate color="primary-dark" class="mt-2"></v-progress-linear>

            <v-alert v-if="hasSearched && !loading && searchResults.length === 0" density="compact" border="start"
              border-color="warning" class="mt-3">
              Aucun compte trouvé pour « {{ searchEmail }} »
            </v-alert>

            <v-list v-if="searchResults.length > 0" class="mt-3 rounded bordered">
              <v-list-item v-for="account in searchResults" :key="account.id"
                :active="selectedAccount?.id === account.id" @click="selectAccount(account)" class="cursor-pointer">
                <template #prepend>
                  <v-icon>mdi-account-circle</v-icon>
                </template>
                <v-list-item-title>{{ account.email }}</v-list-item-title>
                <v-list-item-subtitle>ID compte : {{ account.id }}</v-list-item-subtitle>
                <v-list-item-subtitle>Nombre de commandes : {{ account.nb_commandes }}</v-list-item-subtitle>
                <template #append>
                  <v-icon v-if="selectedAccount?.id === account.id" color="primary-dark">mdi-check-circle</v-icon>
                </template>
              </v-list-item>
            </v-list>

            <div v-if="selectedAccount" class="mt-4">
              <v-alert density="compact" border="start" border-color="primary-dark" class="mb-3">
                Compte sélectionné : <b>{{ selectedAccount.email }}</b>
              </v-alert>
              <SportipsButton icon="mdi-account-arrow-left-outline" @click="confirmReassign" :disabled="reassigning">
                Réassigner l'inscription à ce compte
              </SportipsButton>
            </div>
          </v-col>
        </v-row>

        <v-alert v-if="feedbackMessage" :type="feedbackType" density="compact" class="mt-4" closable>
          {{ feedbackMessage }}
        </v-alert>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapStores } from "pinia";
import { useMainStore } from "@/stores/mainStore";
import AdminService from "@/services/admin.service";

export default {
  name: "ReassignInscription",
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      inscription: {},
      searchEmail: "",
      searchResults: [],
      selectedAccount: null,
      loading: false,
      hasSearched: false,
      reassigning: false,
      feedbackMessage: "",
      feedbackType: "info",
      searchDebounce: null,
    };
  },
  computed: {
    ...mapStores(useMainStore),
  },
  methods: {
    onSearchInput() {
      if (this.searchDebounce) clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        if (this.searchEmail.length >= 2) {
          this.searchAccounts();
        } else {
          this.searchResults = [];
          this.hasSearched = false;
        }
      }, 400);
    },
    async searchAccounts() {
      if (!this.searchEmail || this.searchEmail.trim().length < 2) return;
      this.loading = true;
      this.hasSearched = true;
      this.feedbackMessage = "";
      try {
        const results = await AdminService.searchUserAccountsByEmail(this.searchEmail.trim(), this.mainStore.module.id);
        this.searchResults = Array.isArray(results) ? results : [];
      } catch (err) {
        this.searchResults = [];
      } finally {
        this.loading = false;
      }
    },
    selectAccount(account) {
      this.selectedAccount = account;
      this.feedbackMessage = "";
    },
    async confirmReassign() {
      if (!this.selectedAccount) return;
      this.reassigning = true;
      this.feedbackMessage = "";
      try {
        const success = await AdminService.reassignInscription(
          this.inscription.id,
          this.selectedAccount.id
        );
        if (success) {
          this.feedbackType = "success";
          this.feedbackMessage = "L'inscription et la commande ont été réassignées avec succès.";
          this.mainStore.notification.send("Inscription réassignée avec succès", "success");
          setTimeout(() => window.location.reload(), 1500);
        } else {
          this.feedbackType = "error";
          this.feedbackMessage = "Une erreur est survenue lors de la réassignation.";
          this.mainStore.notification.send("Erreur lors de la réassignation", "error");
        }
      } catch (err) {
        this.feedbackType = "error";
        this.feedbackMessage = "Une erreur est survenue lors de la réassignation.";
        this.mainStore.notification.send("Erreur lors de la réassignation", "error");
      } finally {
        this.reassigning = false;
      }
    },
  },
  async created() {
    this.inscription = { ...this.item };
  },
};
</script>

<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer;
}

.bordered {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
</style>
